<template>
  <div class="machine-learning-page">
    <header class="page-head">
      <div>
        <h2>机器学习</h2>
        <p>内部算法目录、运行流程与训练结果</p>
      </div>
      <div class="algorithm-count"><strong>{{ algorithms.length }}</strong><span>已登记算法</span></div>
    </header>

    <div class="machine-learning-workspace">
      <aside class="algorithm-list" aria-label="机器学习算法列表">
        <div class="algorithm-list__head">
          <strong>算法目录</strong>
          <span>{{ algorithms.length }} 项</span>
        </div>
        <button
          v-for="algorithm in algorithms"
          :key="algorithm.code"
          type="button"
          class="algorithm-item"
          :class="{ 'algorithm-item--active': activeAlgorithm && activeAlgorithm.code === algorithm.code }"
          @click="selectAlgorithm(algorithm)"
        >
          <span class="algorithm-item__topline"><span class="algorithm-type">{{ algorithm.category }}</span><span class="status-badge">{{ statusText(algorithm.status) }}</span></span>
          <strong>{{ algorithm.name }}</strong>
          <small>{{ algorithm.version }} · {{ algorithm.applicableScope }}</small>
        </button>
        <div v-if="!algorithms.length" class="algorithm-list__empty">尚未登记算法</div>
      </aside>

      <main v-if="activeAlgorithm" class="algorithm-detail">
        <section class="algorithm-overview">
          <div class="algorithm-overview__title">
            <div>
              <span class="eyebrow">{{ activeAlgorithm.category }}</span>
              <h3>{{ activeAlgorithm.name }}</h3>
            </div>
            <span class="version-badge">{{ activeAlgorithm.version }}</span>
          </div>
          <p>{{ activeAlgorithm.summary }}</p>
          <dl class="overview-meta">
            <div><dt>适用范围</dt><dd>{{ activeAlgorithm.applicableScope }}</dd></div>
            <div><dt>输出结果</dt><dd>{{ activeAlgorithm.outputDescription }}</dd></div>
          </dl>
        </section>

        <section class="detail-section" aria-labelledby="flow-title">
          <header class="detail-section__head">
            <div>
              <span class="section-index">01</span>
              <h4 id="flow-title">算法流程</h4>
            </div>
            <span>{{ flowDiagramNodes.length }} 个节点</span>
          </header>
          <div class="flow-canvas-panel">
            <div class="flow-canvas-toolbar" aria-label="流程图缩放工具">
              <span class="flow-canvas-zoom">{{ flowZoomPercent }}%</span>
              <div class="flow-canvas-controls">
                <button type="button" class="flow-canvas-button" title="缩小" aria-label="缩小流程图" @click="zoomFlow(1 / 1.2)">−</button>
                <button type="button" class="flow-canvas-button" title="放大" aria-label="放大流程图" @click="zoomFlow(1.2)">+</button>
                <button type="button" class="flow-canvas-button" title="适配画布" aria-label="适配流程图至画布" @click="fitFlowCanvas">⛶</button>
                <button type="button" class="flow-canvas-button" title="重置视图" aria-label="重置流程图视图" @click="resetFlowCanvas">↺</button>
              </div>
            </div>
            <div class="flow-canvas-legend" aria-label="流程图图例">
              <span><i class="flow-legend-swatch flow-legend-swatch--data"></i>数据与清洗</span>
              <span><i class="flow-legend-swatch flow-legend-swatch--model"></i>训练与模型</span>
              <span><i class="flow-legend-swatch flow-legend-swatch--evaluate"></i>评估与发布</span>
              <span><i class="flow-legend-swatch flow-legend-swatch--feedback"></i>虚线为反馈回流</span>
            </div>
            <canvas
              ref="flowCanvas"
              :class="['flow-canvas', { 'flow-canvas--dragging': flowDragging }]"
              tabindex="0"
              aria-label="可拖拽和缩放的蛇类体重趋势预测完整机器学习训练流程图"
              @pointerdown="onFlowPointerDown"
              @pointermove="onFlowPointerMove"
              @pointerup="onFlowPointerEnd"
              @pointercancel="onFlowPointerEnd"
              @wheel.prevent="onFlowWheel"
              @keydown.prevent="onFlowKeydown"
              @dblclick="resetFlowCanvas"
            ></canvas>
          </div>
          <p class="flow-diagram-note">流程图中的训练、独立测试、候选模型选择、评估报告和模型导出已接入 Python 工作流；线上预测仍保持现有版本，需在测试指标达标后单独发布。</p>
        </section>

        <section v-if="activeAlgorithm.code === 'snake-weight-trend'" class="detail-section training-section" aria-labelledby="training-title">
          <header class="detail-section__head">
            <div><span class="section-index">02</span><h4 id="training-title">数据集与训练</h4></div>
            <span>独立测试集评估</span>
          </header>
          <div class="training-steps" aria-label="机器学习工作流程">
            <div><strong>01</strong><span>上传训练集</span></div>
            <div><strong>02</strong><span>上传测试集</span></div>
            <div><strong>03</strong><span>训练候选模型</span></div>
            <div><strong>04</strong><span>查看测试效果</span></div>
          </div>
          <form v-if="canTrain" class="dataset-form" @submit.prevent="runModelTraining">
            <label class="dataset-picker" :class="{ 'dataset-picker--ready': trainingFile }">
              <input type="file" accept="application/json,.json" @change="selectDatasetFile('training', $event)" />
              <span>训练集</span>
              <strong>{{ trainingFile ? trainingFile.name : "选择 JSON 文件" }}</strong>
              <small>{{ trainingFile ? formatFileSize(trainingFile.size) : "用于模型拟合" }}</small>
            </label>
            <label class="dataset-picker" :class="{ 'dataset-picker--ready': testingFile }">
              <input type="file" accept="application/json,.json" @change="selectDatasetFile('testing', $event)" />
              <span>测试集</span>
              <strong>{{ testingFile ? testingFile.name : "选择 JSON 文件" }}</strong>
              <small>{{ testingFile ? formatFileSize(testingFile.size) : "仅用于独立评估" }}</small>
            </label>
            <button class="run-button dataset-form__submit" type="submit" :disabled="trainingSubmitting || !trainingFile || !testingFile">
              {{ trainingSubmitting ? "正在训练与评估..." : "开始训练与测试" }}
            </button>
          </form>
          <p v-else class="training-readonly">当前账号仅可查看模型流程与历史结果。</p>
          <p class="dataset-contract">文件根对象需包含 <code>pets</code> 数组；每只宠物使用体重历史，投喂、温湿度、蜕皮及档案字段可选。单份文件最大 10 MB。</p>
          <p v-if="trainingError" class="prediction-error">{{ trainingError }}</p>
          <p v-if="trainingNotice" class="training-notice">{{ trainingNotice }}</p>

          <div v-if="latestSuccessfulRun" class="training-result" aria-live="polite">
            <div class="training-result__head">
              <div><span>最近测试结果</span><strong>{{ modelName(latestSuccessfulRun.selectedModel) }}</strong></div>
              <span class="run-status run-status--succeeded">已完成</span>
            </div>
            <div class="training-metrics">
              <div><span>训练样本</span><strong>{{ latestSuccessfulRun.trainingSampleCount || "-" }}</strong></div>
              <div><span>测试样本</span><strong>{{ latestSuccessfulRun.testingSampleCount || "-" }}</strong></div>
              <div><span>MAE</span><strong>{{ formatMetric(latestSuccessfulRun.testMae) }}</strong></div>
              <div><span>RMSE</span><strong>{{ formatMetric(latestSuccessfulRun.testRmse) }}</strong></div>
              <div><span>R²</span><strong>{{ formatRatio(latestSuccessfulRun.testRSquared) }}</strong></div>
            </div>
            <p>训练宠物 {{ latestSuccessfulRun.trainingPetCount || "-" }} 只，测试宠物 {{ latestSuccessfulRun.testingPetCount || "-" }} 只，{{ formatHyperparameters(latestSuccessfulRun.selectedHyperparameters) }}</p>
            <div class="model-release">
              <span v-if="latestSuccessfulRun.published" class="model-release__published">当前线上模型 · {{ latestSuccessfulRun.publishedAt ? formatDateTime(latestSuccessfulRun.publishedAt) : "已发布" }}</span>
              <template v-else>
                <span>核对独立测试指标后发布</span>
                <button type="button" class="model-release__button" :disabled="publishingRunId === latestSuccessfulRun.id" @click="publishTrainingRun(latestSuccessfulRun)">
                  {{ publishingRunId === latestSuccessfulRun.id ? "正在发布..." : "发布为线上模型" }}
                </button>
              </template>
            </div>
            <div v-if="latestSuccessfulRun.candidateMetrics && latestSuccessfulRun.candidateMetrics.length" class="candidate-results">
              <span>候选模型对比</span>
              <div class="candidate-results__grid">
                <div v-for="candidate in latestSuccessfulRun.candidateMetrics" :key="candidateKey(candidate)" :class="{ 'candidate-result--selected': isSelectedCandidate(candidate, latestSuccessfulRun) }">
                  <strong>{{ modelName(candidate.modelType) }}</strong>
                  <small>{{ formatHyperparameters(candidate.hyperparameters) }}</small>
                  <em>MAE {{ formatMetric(candidate.metrics && candidate.metrics.mae) }} · R² {{ formatRatio(candidate.metrics && candidate.metrics.r2) }}</em>
                </div>
              </div>
            </div>
            <div v-if="(latestSuccessfulRun.candidateMetrics && latestSuccessfulRun.candidateMetrics.length) || hasTrainingProfileClusters" class="training-charts" aria-label="训练结果图表">
              <section v-if="latestSuccessfulRun.candidateMetrics && latestSuccessfulRun.candidateMetrics.length" class="training-chart">
                <header class="training-chart__head"><strong>候选模型 MAE 对比</strong><span>越低越好</span></header>
                <div ref="candidateMaeChart" class="training-chart__canvas" aria-label="候选模型平均绝对误差柱状图"></div>
              </section>
              <section v-if="latestSuccessfulRun.candidateMetrics && latestSuccessfulRun.candidateMetrics.length" class="training-chart">
                <header class="training-chart__head"><strong>候选模型 R² 对比</strong><span>越高越好</span></header>
                <div ref="candidateR2Chart" class="training-chart__canvas" aria-label="候选模型决定系数柱状图"></div>
              </section>
              <section v-if="hasTrainingProfileClusters" class="training-chart training-chart--wide">
                <header class="training-chart__head"><strong>训练样本画像分组</strong><span>{{ latestSuccessfulRun.trainingProfileClusters.sampleCount }} 个样本 · {{ latestSuccessfulRun.trainingProfileClusters.clusterCount }} 组</span></header>
                <div ref="trainingProfileClusterChart" class="training-chart__canvas training-chart__canvas--profile" aria-label="训练样本平均体重和日均变化聚类散点图"></div>
                <p class="training-chart__note">每个点是一只训练样本；颜色表示体重水平和变化节奏相近的分组，仅用于观察训练数据，不影响线上预测。</p>
              </section>
            </div>
          </div>

          <div class="training-history">
            <div class="training-history__head"><strong>训练记录</strong><button type="button" title="刷新训练记录" aria-label="刷新训练记录" :disabled="trainingRunsLoading" @click="loadTrainingRuns">↻</button></div>
            <div v-if="trainingRunsLoading" class="training-history__empty">正在读取训练记录...</div>
            <div v-else-if="!trainingRuns.length" class="training-history__empty">尚无训练记录</div>
            <div v-else class="training-history__table-wrap">
              <table>
                <thead><tr><th>状态</th><th>训练集 / 测试集</th><th>候选结果</th><th>测试指标</th><th>线上版本</th><th>完成时间</th></tr></thead>
                <tbody>
                  <tr v-for="run in trainingRuns" :key="run.id">
                    <td><span class="run-status" :class="runStatusClass(run.status)">{{ runStatusText(run.status) }}</span></td>
                    <td><strong>{{ run.trainingFileName }}</strong><small>{{ run.testingFileName }}</small></td>
                    <td><template v-if="run.status === 'SUCCEEDED'"><strong>{{ modelName(run.selectedModel) }}</strong><small>{{ formatHyperparameters(run.selectedHyperparameters) }}</small></template><span v-else>{{ run.failureMessage || "等待执行" }}</span></td>
                    <td>{{ run.status === 'SUCCEEDED' ? `MAE ${formatMetric(run.testMae)} · R² ${formatRatio(run.testRSquared)}` : "-" }}</td>
                    <td>
                      <span v-if="run.published" class="run-status run-status--published">已发布</span>
                      <button v-else-if="canTrain && run.status === 'SUCCEEDED'" type="button" class="history-publish-button" :disabled="publishingRunId === run.id" @click="publishTrainingRun(run)">
                        {{ publishingRunId === run.id ? "发布中..." : "发布" }}
                      </button>
                      <span v-else>-</span>
                    </td>
                    <td>{{ formatDateTime(run.completedAt || run.createdAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section v-if="activeAlgorithm.code === 'snake-weight-trend'" class="detail-section prediction-section" aria-labelledby="prediction-title">
          <header class="detail-section__head">
            <div>
              <span class="section-index">03</span>
              <h4 id="prediction-title">趋势预测</h4>
            </div>
            <span>已发布模型优先</span>
          </header>
          <div class="prediction-form">
            <label>
              <span>蛇类对象</span>
              <select v-model="selectedPetId" :disabled="petLoading || !snakePets.length">
                <option value="">{{ petLoading ? "正在读取宠物..." : snakePets.length ? "选择一只蛇类宠物" : "暂无已归类的蛇类宠物" }}</option>
                <option v-for="pet in snakePets" :key="pet.id" :value="String(pet.id)">
                  {{ pet.name || "未命名宠物" }} · {{ pet.species || pet.speciesCode || `ID ${pet.id}` }}
                </option>
              </select>
            </label>
            <label>
              <span>预测周期（天）</span>
              <input v-model.number="predictionHorizonDays" type="number" min="1" max="30" step="1" />
            </label>
            <button class="run-button" type="button" :disabled="predicting || !selectedPetId" @click="runPrediction">
              {{ predicting ? "计算中..." : "运行预测" }}
            </button>
          </div>
          <p v-if="predictionError" class="prediction-error">{{ predictionError }}</p>
          <div v-if="prediction" class="prediction-result" aria-live="polite">
            <div class="prediction-result__headline">
              <span>{{ predictionSourceText(prediction.inferenceSource) }}</span>
              <strong>{{ prediction.petName }} · {{ prediction.predictionHorizonDays }} 天后</strong>
            </div>
            <div class="prediction-metrics">
              <div><span>预测体重</span><strong>{{ formatWeight(prediction.predictedWeightValue) }}</strong></div>
              <div><span>预计变化</span><strong :class="trendClass(prediction.trend)">{{ signedWeight(prediction.predictedChangeValue) }}</strong></div>
              <div><span>日均趋势</span><strong>{{ signedDailyWeight(prediction.estimatedDailyChangeValue) }}</strong></div>
              <div><span>{{ prediction.inferenceSource === 'PUBLISHED_MODEL' ? "独立测试 R²" : "拟合优度 R²" }}</span><strong>{{ formatRatio(prediction.rSquared) }}</strong></div>
            </div>
            <div class="prediction-result__foot">
              <span>{{ prediction.inferenceSource === 'PUBLISHED_MODEL' ? `线上 ${modelName(prediction.modelType)} · 测试 MAE ${formatMetric(prediction.modelTestMae)}` : `体重记录 ${prediction.effectiveSampleCount} 条` }} · {{ formatDate(prediction.trainingStartDate) }} 至 {{ formatDate(prediction.trainingEndDate) }}</span>
              <span class="confidence-badge" :class="confidenceClass(prediction.confidence)">{{ confidenceText(prediction.confidence) }}置信</span>
            </div>
            <section v-if="prediction.observedWeightSeries && prediction.observedWeightSeries.length" class="prediction-chart" aria-label="体重历史与预测趋势图">
              <header class="training-chart__head"><strong>历史体重与预测趋势</strong><span>单位：g</span></header>
              <div ref="predictionTrendChart" class="prediction-chart__canvas" aria-label="体重历史与预测趋势折线图"></div>
            </section>
          </div>
          <p class="prediction-note">预测结果仅用于观察体重趋势，不代替健康评估、兽医诊断或喂食决策。</p>
        </section>

        <section class="detail-section limitations-section" aria-labelledby="limitation-title">
          <header class="detail-section__head">
            <div><span class="section-index">04</span><h4 id="limitation-title">使用边界</h4></div>
          </header>
          <ul><li v-for="item in activeAlgorithm.limitations" :key="item">{{ item }}</li></ul>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts/core"
import { BarChart, LineChart, ScatterChart } from "echarts/charts"
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components"
import { CanvasRenderer } from "echarts/renderers"
import { fetchPetList } from "../api/pet"
import { fetchMachineLearningAlgorithms, fetchSnakeWeightTrainingRuns, predictSnakeWeightTrend, publishSnakeWeightModel, trainSnakeWeightModel } from "../api/machineLearning"
import { hasAdminPermission } from "../utils/adminSession"

echarts.use([BarChart, LineChart, ScatterChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer])

function createBuiltInAlgorithms() {
  return [{
    code: "snake-weight-trend",
    name: "蛇类体重趋势预测",
    version: "v1.1",
    category: "监督学习 / 岭回归",
    status: "ACTIVE",
    summary: "使用独立训练集和测试集学习蛇类体重变化，审核后发布为线上预测模型。",
    applicableScope: "体重历史包含至少 3 条不同日期有效记录的蛇类数据集；完整训练集至少需要 12 条监督样本。",
    outputDescription: "测试集 MAE、RMSE、R²、候选模型选择结果、已发布模型版本和单宠物趋势预测。",
    flowSteps: [
      { sequence: 1, title: "读取记录", description: "读取该宠物最近的体重记录和测量时间。" },
      { sequence: 2, title: "样本校验", description: "过滤无效数据，至少保留 4 个不同日期。" },
      { sequence: 3, title: "数据整理", description: "同日仅保留最后一次，截取最近 90 天数据。" },
      { sequence: 4, title: "特征构建", description: "日期转换为训练天数，体重作为学习目标。" },
      { sequence: 5, title: "线性回归拟合", description: "最小二乘法计算每日变化斜率和趋势线。" },
      { sequence: 6, title: "预测与评估", description: "延长趋势线，输出预测值、R² 和置信等级。" }
    ],
    limitations: [
      "已发布模型可使用体重、投喂、温湿度、蜕皮及档案特征；缺失字段会按训练期规则补全。",
      "结果仅用于趋势观察，不能替代兽医判断，也不应直接作为喂食或治疗决策。"
    ]
  }]
}

const snakeWeightPipeline = {
  nodes: [
    { id: "source", type: "source", x: 640, y: 24, width: 420, height: 94, title: "数据集上传与采集", lines: ["训练集 JSON | 独立测试集 JSON", "体重 | 投喂 | 温湿度 | 蜕皮 | 档案"] },
    { id: "clean", type: "process", x: 700, y: 150, width: 300, height: 82, title: "数据清洗", lines: ["统一日期、重量单位与字段格式", "过滤空值并保留原始记录版本"] },
    { id: "missing", type: "process", x: 70, y: 290, width: 360, height: 92, title: "缺失值处理", lines: ["体重、温度等字段缺失", "按字段规则补全或标记不可用"] },
    { id: "outlier", type: "process", x: 530, y: 290, width: 400, height: 92, title: "异常值过滤", lines: ["识别不合理体重、温度与喂食记录", "剔除异常样本并保留处理原因"] },
    { id: "feature-source", type: "feature", x: 1030, y: 290, width: 460, height: 92, title: "特征构造", lines: ["最近 3 次体重、喂食间隔、距蜕皮天数", "按测量时间对齐多源记录"] },
    { id: "split", type: "data", x: 690, y: 455, width: 320, height: 88, title: "数据集确认", lines: ["训练集与测试集分别上传", "测试集不参与参数拟合"] },
    { id: "train-set", type: "data", x: 230, y: 610, width: 390, height: 92, title: "训练集", lines: ["用于学习体重变化规律", "仅在训练阶段参与参数拟合"] },
    { id: "test-set", type: "data", x: 1080, y: 610, width: 390, height: 92, title: "独立测试集", lines: ["完全不参与训练", "用于评估真实泛化效果"] },
    { id: "engineering", type: "feature", x: 230, y: 770, width: 390, height: 92, title: "特征工程", lines: ["将清洗后的训练集转换为模型输入", "同步生成监督学习标签 ΔW"] },
    { id: "numeric", type: "feature", x: 20, y: 930, width: 360, height: 92, title: "数值特征", lines: ["体重 | 猎物重 | 温度", "最近 3 次体重与变化速度"] },
    { id: "categorical", type: "feature", x: 420, y: 930, width: 360, height: 92, title: "类别特征", lines: ["蛇品种 | 性别 | 生长阶段", "编码后与数值特征合并"] },
    { id: "label", type: "feature", x: 820, y: 930, width: 360, height: 92, title: "监督学习标签 Y", lines: ["体重变化 ΔW", "当前记录与下一观测体重的差值"] },
    { id: "selection", type: "model", x: 1220, y: 930, width: 390, height: 92, title: "候选模型", lines: ["均值基线 | 岭回归", "按独立测试集 MAE 选择"] },
    { id: "training", type: "model", x: 1220, y: 1080, width: 390, height: 92, title: "模型训练", lines: ["仅使用上传训练集拟合", "保存训练版本与数据快照"] },
    { id: "tuning", type: "model", x: 1220, y: 1230, width: 390, height: 108, title: "正则参数调优", lines: ["比较多个岭回归 alpha", "最小化独立测试集 MAE"] },
    { id: "evaluation", type: "evaluate", x: 1220, y: 1400, width: 390, height: 92, title: "模型评估（测试集）", lines: ["测试集不参与训练", "生成可复现的评估报告"] },
    { id: "metrics", type: "evaluate", x: 730, y: 1560, width: 430, height: 100, title: "评估指标", lines: ["MAE 平均绝对误差 | RMSE 均方根误差", "同时记录 R²、样本量和评估时间"] },
    { id: "overfit", type: "decision", x: 1260, y: 1550, width: 330, height: 130, title: "是否过拟合？", lines: ["训练集与测试集误差对比"] },
    { id: "export", type: "output", x: 1220, y: 1740, width: 390, height: 92, title: "模型导出", lines: ["保存模型文件与版本元数据", "模型文件格式：JSON"] },
    { id: "deploy", type: "output", x: 1220, y: 1890, width: 390, height: 100, title: "审核后发布到 APP", lines: ["通过测试指标后单独发布", "避免未评估模型影响线上预测"] },
    { id: "feedback", type: "source", x: 1190, y: 2050, width: 450, height: 100, title: "线上真实反馈数据回流", lines: ["新增喂食记录、真实体重与环境数据", "进入下一轮训练，迭代更新数据集"] }
  ],
  edges: [
    { key: "source-clean", from: { x: 850, y: 118 }, to: { x: 850, y: 150 } },
    { key: "clean-missing", from: { x: 750, y: 232 }, control1: { x: 650, y: 260 }, control2: { x: 380, y: 260 }, to: { x: 250, y: 290 } },
    { key: "clean-outlier", from: { x: 850, y: 232 }, to: { x: 730, y: 290 } },
    { key: "clean-feature-source", from: { x: 950, y: 232 }, control1: { x: 1040, y: 260 }, control2: { x: 1180, y: 260 }, to: { x: 1260, y: 290 } },
    { key: "missing-split", from: { x: 250, y: 382 }, control1: { x: 250, y: 430 }, control2: { x: 610, y: 420 }, to: { x: 690, y: 500 } },
    { key: "outlier-split", from: { x: 730, y: 382 }, to: { x: 800, y: 455 } },
    { key: "feature-source-split", from: { x: 1260, y: 382 }, control1: { x: 1260, y: 430 }, control2: { x: 1010, y: 420 }, to: { x: 1010, y: 500 } },
    { key: "split-train", from: { x: 750, y: 543 }, control1: { x: 700, y: 580 }, control2: { x: 520, y: 575 }, to: { x: 425, y: 610 }, label: "80%", labelX: 590, labelY: 575 },
    { key: "split-test", from: { x: 950, y: 543 }, control1: { x: 1000, y: 580 }, control2: { x: 1190, y: 575 }, to: { x: 1275, y: 610 }, label: "20%", labelX: 1110, labelY: 575 },
    { key: "train-engineering", from: { x: 425, y: 702 }, to: { x: 425, y: 770 } },
    { key: "engineering-numeric", from: { x: 340, y: 862 }, control1: { x: 260, y: 900 }, control2: { x: 200, y: 895 }, to: { x: 200, y: 930 } },
    { key: "engineering-categorical", from: { x: 425, y: 862 }, to: { x: 600, y: 930 } },
    { key: "engineering-label", from: { x: 510, y: 862 }, control1: { x: 610, y: 900 }, control2: { x: 900, y: 895 }, to: { x: 1000, y: 930 } },
    { key: "engineering-selection", from: { x: 620, y: 815 }, control1: { x: 900, y: 850 }, control2: { x: 1200, y: 850 }, to: { x: 1415, y: 930 } },
    { key: "numeric-selection", from: { x: 380, y: 976 }, control1: { x: 700, y: 1010 }, control2: { x: 1100, y: 990 }, to: { x: 1415, y: 930 } },
    { key: "categorical-selection", from: { x: 780, y: 976 }, control1: { x: 980, y: 1010 }, control2: { x: 1250, y: 990 }, to: { x: 1415, y: 930 } },
    { key: "label-selection", from: { x: 1180, y: 976 }, to: { x: 1415, y: 930 } },
    { key: "selection-training", from: { x: 1415, y: 1022 }, to: { x: 1415, y: 1080 } },
    { key: "training-tuning", from: { x: 1415, y: 1172 }, to: { x: 1415, y: 1230 } },
    { key: "tuning-evaluation", from: { x: 1415, y: 1341 }, to: { x: 1415, y: 1400 } },
    { key: "evaluation-metrics", from: { x: 1220, y: 1446 }, control1: { x: 1190, y: 1500 }, control2: { x: 1040, y: 1500 }, to: { x: 1160, y: 1610 } },
    { key: "evaluation-overfit", from: { x: 1460, y: 1492 }, to: { x: 1460, y: 1550 } },
    { key: "overfit-export", from: { x: 1425, y: 1680 }, control1: { x: 1425, y: 1710 }, control2: { x: 1415, y: 1710 }, to: { x: 1415, y: 1740 }, label: "否 / 达标", labelX: 1510, labelY: 1710 },
    { key: "export-deploy", from: { x: 1415, y: 1832 }, to: { x: 1415, y: 1890 } },
    { key: "deploy-feedback", from: { x: 1415, y: 1990 }, to: { x: 1415, y: 2050 } },
    { key: "overfit-selection", from: { x: 1260, y: 1610 }, control1: { x: 1110, y: 1610 }, control2: { x: 1090, y: 960 }, to: { x: 1220, y: 976 }, feedback: true, label: "是 / 重新调参", labelX: 1110, labelY: 1280 },
    { key: "test-feedback", from: { x: 1275, y: 702 }, control1: { x: 1680, y: 730 }, control2: { x: 1680, y: 2080 }, to: { x: 1640, y: 2100 }, feedback: true },
    { key: "feedback-source", from: { x: 1190, y: 2100 }, control1: { x: 1080, y: 2210 }, control2: { x: 520, y: 2210 }, to: { x: 640, y: 70 }, feedback: true }
  ]
}

const FLOW_DIAGRAM_WIDTH = 1700
const FLOW_DIAGRAM_HEIGHT = 2200
const FLOW_MIN_SCALE = 0.12
const FLOW_MAX_SCALE = 2.4

export default {
  name: "MachineLearningView",
  data() {
    const algorithms = createBuiltInAlgorithms()
    return {
      algorithms,
      activeAlgorithm: algorithms[0],
      loading: false,
      petLoading: false,
      pets: [],
      selectedPetId: "",
      predictionHorizonDays: 14,
      predicting: false,
      prediction: null,
      predictionError: "",
      trainingFile: null,
      testingFile: null,
      trainingSubmitting: false,
      publishingRunId: null,
      trainingRunsLoading: false,
      trainingRuns: [],
      trainingError: "",
      trainingNotice: "",
      flowCanvasSize: { width: 0, height: 0, dpr: 1 },
      flowViewport: { scale: 1, offsetX: 0, offsetY: 0 },
      flowPointers: {},
      flowPanState: null,
      flowPinchState: null,
      flowDragging: false,
      flowWindowResizeHandler: null
    }
  },
  computed: {
    snakePets() {
      return this.pets.filter(pet => this.isSnakePet(pet))
    },
    canTrain() {
      return hasAdminPermission("office:ml:train")
    },
    latestSuccessfulRun() {
      return this.trainingRuns.find(run => run.status === "SUCCEEDED") || null
    },
    hasTrainingProfileClusters() {
      const profile = this.latestSuccessfulRun && this.latestSuccessfulRun.trainingProfileClusters
      return Boolean(profile && Array.isArray(profile.points) && profile.points.length >= 2)
    },
    flowDiagramNodes() {
      if (this.activeAlgorithm && this.activeAlgorithm.code === "snake-weight-trend") {
        return snakeWeightPipeline.nodes
      }
      const positions = [
        { x: 24, y: 30 }, { x: 388, y: 30 }, { x: 752, y: 30 },
        { x: 752, y: 236 }, { x: 388, y: 236 }, { x: 24, y: 236 }
      ]
      const steps = this.activeAlgorithm && Array.isArray(this.activeAlgorithm.flowSteps)
        ? this.activeAlgorithm.flowSteps : []
      return steps.slice(0, positions.length).map((step, index) => ({
        ...step,
        ...positions[index],
        id: step.sequence,
        type: "process",
        width: 284,
        height: 144,
        lines: this.splitFlowDescription(step.description)
      }))
    },
    flowDiagramEdges() {
      if (this.activeAlgorithm && this.activeAlgorithm.code === "snake-weight-trend") {
        return snakeWeightPipeline.edges
      }
      const nodes = this.flowDiagramNodes
      return nodes.slice(0, -1).map((node, index) => ({
        key: `${node.sequence}-${nodes[index + 1].sequence}`,
        from: { x: node.x + node.width, y: node.y + node.height / 2 },
        to: { x: nodes[index + 1].x, y: nodes[index + 1].y + nodes[index + 1].height / 2 }
      }))
    },
    flowZoomPercent() {
      return Math.round(this.flowViewport.scale * 100)
    }
  },
  created() {
    this.loadPage()
  },
  mounted() {
    this.$nextTick(() => {
      this.initializeFlowCanvas()
      this.renderTrainingCharts()
      this.renderPredictionChart()
    })
  },
  beforeDestroy() {
    if (this.flowWindowResizeHandler) window.removeEventListener("resize", this.flowWindowResizeHandler)
    this.disposeChart("candidateMaeChart")
    this.disposeChart("candidateR2Chart")
    this.disposeChart("trainingProfileClusterChart")
    this.disposeChart("predictionTrendChart")
  },
  watch: {
    activeAlgorithm() {
      this.$nextTick(() => this.fitFlowCanvas())
    },
    trainingRuns() {
      this.$nextTick(() => this.renderTrainingCharts())
    },
    prediction() {
      this.$nextTick(() => this.renderPredictionChart())
    }
  },
  methods: {
    async loadPage() {
      this.loading = true
      try {
        const result = await fetchMachineLearningAlgorithms()
        this.applyAlgorithmCatalog(result.data)
      } catch (error) {
        // Keep the built-in algorithm documentation visible when the catalog cannot sync.
      } finally {
        this.loading = false
      }
      await Promise.all([this.loadPets(), this.loadTrainingRuns()])
    },
    async loadPets() {
      this.petLoading = true
      try {
        const result = await fetchPetList()
        this.pets = Array.isArray(result.data) ? result.data : []
      } catch (error) {
        this.pets = []
      } finally {
        this.petLoading = false
      }
    },
    async loadTrainingRuns() {
      this.trainingRunsLoading = true
      try {
        const result = await fetchSnakeWeightTrainingRuns()
        this.trainingRuns = Array.isArray(result.data) ? result.data : []
      } catch (error) {
        this.trainingRuns = []
      } finally {
        this.trainingRunsLoading = false
      }
    },
    selectDatasetFile(kind, event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ""
      if (!file) return
      if (!file.name.toLowerCase().endsWith(".json")) {
        this.trainingError = "训练集和测试集均需选择 JSON 文件"
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.trainingError = "单份数据集不能超过 10 MB"
        return
      }
      if (kind === "training") this.trainingFile = file
      else this.testingFile = file
      this.trainingError = ""
      this.trainingNotice = ""
    },
    async runModelTraining() {
      if (!this.trainingFile || !this.testingFile) {
        this.trainingError = "请分别选择训练集和测试集"
        return
      }
      this.trainingSubmitting = true
      this.trainingError = ""
      this.trainingNotice = ""
      try {
        const result = await trainSnakeWeightModel(this.trainingFile, this.testingFile)
        const run = result.data
        if (run) this.trainingRuns = [run, ...this.trainingRuns.filter(item => item.id !== run.id)]
        this.trainingFile = null
        this.testingFile = null
        this.trainingNotice = "训练与独立测试已完成，结果已归档。"
      } catch (error) {
        this.trainingError = error.message || "训练任务失败"
        await this.loadTrainingRuns()
      } finally {
        this.trainingSubmitting = false
      }
    },
    async publishTrainingRun(run) {
      if (!run || run.status !== "SUCCEEDED" || !run.id) return
      this.publishingRunId = run.id
      this.trainingError = ""
      this.trainingNotice = ""
      try {
        await publishSnakeWeightModel(run.id)
        await this.loadTrainingRuns()
        this.trainingNotice = "模型已发布，趋势预测将优先使用该线上版本。"
      } catch (error) {
        this.trainingError = error.message || "模型发布失败"
      } finally {
        this.publishingRunId = null
      }
    },
    selectAlgorithm(algorithm) {
      this.activeAlgorithm = algorithm
      this.prediction = null
      this.predictionError = ""
    },
    applyAlgorithmCatalog(remoteAlgorithms) {
      if (!Array.isArray(remoteAlgorithms)) return
      const currentCode = this.activeAlgorithm && this.activeAlgorithm.code
      const remote = remoteAlgorithms.filter(algorithm => algorithm && algorithm.code)
      const remoteCodes = new Set(remote.map(algorithm => algorithm.code))
      this.algorithms = remote.concat(createBuiltInAlgorithms().filter(algorithm => !remoteCodes.has(algorithm.code)))
      this.activeAlgorithm = this.algorithms.find(algorithm => algorithm.code === currentCode) || this.algorithms[0] || null
    },
    splitFlowDescription(value) {
      const characters = Array.from(String(value || ""))
      const lines = []
      while (characters.length && lines.length < 2) {
        lines.push(characters.splice(0, 17).join(""))
      }
      return lines
    },
    initializeFlowCanvas() {
      const canvas = this.$refs.flowCanvas
      if (!canvas) return
      this.resizeFlowCanvas(true)
      this.flowWindowResizeHandler = () => window.requestAnimationFrame(() => {
        this.resizeFlowCanvas(true)
        this.resizeCharts()
      })
      window.addEventListener("resize", this.flowWindowResizeHandler)
    },
    chartInstance(name) {
      const element = this.$refs[name]
      if (!element) {
        this.disposeChart(name)
        return null
      }
      const current = this[name]
      if (current && current.getDom && current.getDom() === element) return current
      if (current && current.dispose) current.dispose()
      const chart = echarts.init(element)
      this[name] = chart
      return chart
    },
    disposeChart(name) {
      const chart = this[name]
      if (chart && chart.dispose) chart.dispose()
      this[name] = null
    },
    resizeCharts() {
      ["candidateMaeChart", "candidateR2Chart", "trainingProfileClusterChart", "predictionTrendChart"].forEach(name => {
        const chart = this[name]
        if (chart && chart.resize) chart.resize()
      })
    },
    candidateLabel(candidate) {
      if (!candidate) return "候选模型"
      if (candidate.modelType !== "ridge_regression") return "均值基线"
      const alpha = candidate.hyperparameters && candidate.hyperparameters.alpha
      return alpha == null ? "岭回归" : `岭回归 α=${alpha}`
    },
    numericMetric(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    renderTrainingCharts() {
      const candidates = this.latestSuccessfulRun && Array.isArray(this.latestSuccessfulRun.candidateMetrics)
        ? this.latestSuccessfulRun.candidateMetrics : []
      this.renderTrainingProfileClusterChart()
      if (!candidates.length) {
        this.disposeChart("candidateMaeChart")
        this.disposeChart("candidateR2Chart")
        return
      }
      const labels = candidates.map(candidate => this.candidateLabel(candidate))
      const maeValues = candidates.map(candidate => this.numericMetric(candidate.metrics && candidate.metrics.mae))
      const r2Values = candidates.map(candidate => this.numericMetric(candidate.metrics && candidate.metrics.r2))
      const maeChart = this.chartInstance("candidateMaeChart")
      if (maeChart) {
        maeChart.setOption({
          animationDuration: 320,
          grid: { top: 8, right: 18, bottom: 22, left: 96, containLabel: false },
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: value => `${Number(value).toFixed(4)} g/天` },
          xAxis: { type: "value", splitLine: { lineStyle: { color: "#e8efec" } }, axisLabel: { color: "#718095", fontSize: 10 } },
          yAxis: { type: "category", data: labels, axisTick: { show: false }, axisLine: { show: false }, axisLabel: { color: "#536578", fontSize: 10 } },
          series: [{ type: "bar", data: maeValues, barMaxWidth: 20, itemStyle: { color: "#2f8763", borderRadius: [0, 3, 3, 0] }, label: { show: true, position: "right", color: "#506173", fontSize: 10, formatter: params => Number(params.value).toFixed(4) } }]
        }, true)
      }
      const r2Chart = this.chartInstance("candidateR2Chart")
      if (r2Chart) {
        r2Chart.setOption({
          animationDuration: 320,
          grid: { top: 8, right: 18, bottom: 22, left: 96, containLabel: false },
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: value => Number(value).toFixed(4) },
          xAxis: { type: "value", splitLine: { lineStyle: { color: "#e8efec" } }, axisLabel: { color: "#718095", fontSize: 10 } },
          yAxis: { type: "category", data: labels, axisTick: { show: false }, axisLine: { show: false }, axisLabel: { color: "#536578", fontSize: 10 } },
          series: [{ type: "bar", data: r2Values, barMaxWidth: 20, itemStyle: { color: params => Number(params.value) >= 0 ? "#2f8763" : "#c78746", borderRadius: [0, 3, 3, 0] }, label: { show: true, position: "right", color: "#506173", fontSize: 10, formatter: params => Number(params.value).toFixed(3) } }]
        }, true)
      }
    },
    renderTrainingProfileClusterChart() {
      const profile = this.latestSuccessfulRun && this.latestSuccessfulRun.trainingProfileClusters
      const points = profile && Array.isArray(profile.points) ? profile.points : []
      const clusters = profile && Array.isArray(profile.clusters) ? profile.clusters : []
      if (points.length < 2) {
        this.disposeChart("trainingProfileClusterChart")
        return
      }
      const clusterById = new Map(clusters.map(cluster => [cluster.clusterId, cluster]))
      const grouped = new Map()
      points.forEach(point => {
        const averageWeight = this.numericMetric(point.averageWeightG)
        const averageDailyChange = this.numericMetric(point.averageDailyChangeG)
        if (averageWeight == null || averageDailyChange == null) return
        const clusterId = point.clusterId || "ungrouped"
        if (!grouped.has(clusterId)) grouped.set(clusterId, [])
        grouped.get(clusterId).push({
          value: [averageWeight, averageDailyChange],
          sampleLabel: point.sampleLabel || `样本 ${point.petId || "-"}`
        })
      })
      if (!grouped.size) {
        this.disposeChart("trainingProfileClusterChart")
        return
      }
      const colors = ["#2f8763", "#3e87a9", "#c78746"]
      const chart = this.chartInstance("trainingProfileClusterChart")
      if (!chart) return
      chart.setOption({
        animationDuration: 320,
        color: colors,
        tooltip: {
          trigger: "item",
          formatter: params => `${params.marker}${params.seriesName}<br/>${params.data.sampleLabel}<br/>平均体重：${Number(params.value[0]).toFixed(2)} g<br/>日均变化：${Number(params.value[1]).toFixed(4)} g/天`
        },
        legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 7, textStyle: { color: "#647589", fontSize: 10 } },
        grid: { top: 34, right: 24, bottom: 34, left: 54 },
        xAxis: {
          type: "value",
          name: "平均体重 (g)",
          nameLocation: "middle",
          nameGap: 24,
          scale: true,
          splitLine: { lineStyle: { color: "#e8efec" } },
          axisLabel: { color: "#718095", fontSize: 10 }
        },
        yAxis: {
          type: "value",
          name: "日均变化 (g/天)",
          nameLocation: "middle",
          nameGap: 38,
          scale: true,
          splitLine: { lineStyle: { color: "#e8efec" } },
          axisLabel: { color: "#718095", fontSize: 10 }
        },
        series: Array.from(grouped.entries()).map(([clusterId, items], index) => {
          const cluster = clusterById.get(clusterId)
          return {
            name: (cluster && cluster.label) || "画像组",
            type: "scatter",
            data: items,
            symbolSize: 13,
            label: {
              show: items.length <= 5,
              position: "top",
              color: "#526476",
              fontSize: 10,
              formatter: params => params.data.sampleLabel
            },
            itemStyle: { borderColor: "#fff", borderWidth: 1.5, opacity: .9 },
            emphasis: { scale: 1.2 }
          }
        })
      }, true)
    },
    renderPredictionChart() {
      const prediction = this.prediction
      const observed = prediction && Array.isArray(prediction.observedWeightSeries) ? prediction.observedWeightSeries : []
      if (!observed.length) {
        this.disposeChart("predictionTrendChart")
        return
      }
      const forecast = Array.isArray(prediction.forecastWeightSeries) ? prediction.forecastWeightSeries : []
      const labels = Array.from(new Set(observed.concat(forecast)
        .map(point => this.formatDate(point.measuredAt))
        .filter(Boolean)))
      const observedByDate = new Map(observed.map(point => [this.formatDate(point.measuredAt), this.numericMetric(point.weightValue)]))
      const forecastByDate = new Map(forecast.map(point => [this.formatDate(point.measuredAt), this.numericMetric(point.weightValue)]))
      const chart = this.chartInstance("predictionTrendChart")
      if (!chart) return
      chart.setOption({
        animationDuration: 320,
        color: ["#2f8763", "#c78746"],
        tooltip: { trigger: "axis", valueFormatter: value => `${Number(value).toFixed(2)} g` },
        legend: { right: 0, top: 0, itemWidth: 10, itemHeight: 6, textStyle: { color: "#647589", fontSize: 10 } },
        grid: { top: 34, right: 14, bottom: 28, left: 44 },
        xAxis: { type: "category", data: labels, boundaryGap: false, axisLine: { lineStyle: { color: "#cbd9d5" } }, axisLabel: { color: "#718095", fontSize: 10 } },
        yAxis: { type: "value", scale: true, splitLine: { lineStyle: { color: "#e8efec" } }, axisLabel: { color: "#718095", fontSize: 10, formatter: value => `${value} g` } },
        series: [
          { name: "历史体重", type: "line", data: labels.map(label => observedByDate.has(label) ? observedByDate.get(label) : "-"), connectNulls: false, showSymbol: true, symbolSize: 6, lineStyle: { width: 2 } },
          { name: "模型预测", type: "line", data: labels.map(label => forecastByDate.has(label) ? forecastByDate.get(label) : "-"), connectNulls: false, showSymbol: true, symbol: "diamond", symbolSize: 7, lineStyle: { width: 2, type: "dashed" } }
        ]
      }, true)
    },
    resizeFlowCanvas(shouldFit) {
      const canvas = this.$refs.flowCanvas
      if (!canvas) return
      const bounds = canvas.getBoundingClientRect()
      const width = Math.round(bounds.width)
      const height = Math.round(bounds.height)
      if (!width || !height) return
      const dpr = window.devicePixelRatio || 1
      const needsResize = width !== this.flowCanvasSize.width || height !== this.flowCanvasSize.height || dpr !== this.flowCanvasSize.dpr
      if (needsResize) {
        canvas.width = Math.round(width * dpr)
        canvas.height = Math.round(height * dpr)
        this.flowCanvasSize = { width, height, dpr }
      }
      if (shouldFit || needsResize || !this.flowViewport.scale) this.fitFlowCanvas()
      else this.drawFlowCanvas()
    },
    fitFlowCanvas() {
      const canvas = this.$refs.flowCanvas
      if (!canvas) return
      const width = canvas.clientWidth
      const height = canvas.clientHeight
      if (!width || !height) return
      const diagramWidth = FLOW_DIAGRAM_WIDTH
      const diagramHeight = FLOW_DIAGRAM_HEIGHT
      const padding = 48
      const scale = Math.min((width - padding * 2) / diagramWidth, (height - padding * 2) / diagramHeight)
      this.flowViewport.scale = Math.max(FLOW_MIN_SCALE, Math.min(scale, FLOW_MAX_SCALE))
      this.flowViewport.offsetX = (width - diagramWidth * this.flowViewport.scale) / 2
      this.flowViewport.offsetY = (height - diagramHeight * this.flowViewport.scale) / 2
      this.drawFlowCanvas()
    },
    resetFlowCanvas() {
      this.fitFlowCanvas()
    },
    zoomFlow(factor) {
      const canvas = this.$refs.flowCanvas
      if (!canvas) return
      this.zoomFlowAt({ x: canvas.clientWidth / 2, y: canvas.clientHeight / 2 }, factor)
    },
    zoomFlowAt(point, factor) {
      const previousScale = this.flowViewport.scale
      const nextScale = Math.max(FLOW_MIN_SCALE, Math.min(previousScale * factor, FLOW_MAX_SCALE))
      if (nextScale === previousScale) return
      const worldX = (point.x - this.flowViewport.offsetX) / previousScale
      const worldY = (point.y - this.flowViewport.offsetY) / previousScale
      this.flowViewport.scale = nextScale
      this.flowViewport.offsetX = point.x - worldX * nextScale
      this.flowViewport.offsetY = point.y - worldY * nextScale
      this.drawFlowCanvas()
    },
    getFlowCanvasPoint(event) {
      const rect = this.$refs.flowCanvas.getBoundingClientRect()
      return { x: event.clientX - rect.left, y: event.clientY - rect.top }
    },
    getFlowPointers() {
      return Object.keys(this.flowPointers).map(key => this.flowPointers[key])
    },
    onFlowPointerDown(event) {
      if (event.pointerType === "mouse" && event.button !== 0) return
      const canvas = this.$refs.flowCanvas
      canvas.setPointerCapture(event.pointerId)
      const point = this.getFlowCanvasPoint(event)
      this.$set(this.flowPointers, event.pointerId, point)
      const pointers = this.getFlowPointers()
      this.flowDragging = true
      if (pointers.length === 1) {
        this.flowPanState = { point, offsetX: this.flowViewport.offsetX, offsetY: this.flowViewport.offsetY }
        this.flowPinchState = null
      } else if (pointers.length === 2) {
        this.flowPanState = null
        this.flowPinchState = this.createPinchState(pointers)
      }
    },
    onFlowPointerMove(event) {
      if (!this.flowPointers[event.pointerId]) return
      this.$set(this.flowPointers, event.pointerId, this.getFlowCanvasPoint(event))
      const pointers = this.getFlowPointers()
      if (pointers.length >= 2 && this.flowPinchState) {
        const center = this.flowPointerCenter(pointers)
        const distance = this.flowPointerDistance(pointers)
        const scale = Math.max(FLOW_MIN_SCALE, Math.min(this.flowPinchState.scale * distance / this.flowPinchState.distance, FLOW_MAX_SCALE))
        this.flowViewport.scale = scale
        this.flowViewport.offsetX = center.x - this.flowPinchState.worldX * scale
        this.flowViewport.offsetY = center.y - this.flowPinchState.worldY * scale
        this.drawFlowCanvas()
      } else if (this.flowPanState) {
        const point = pointers[0]
        this.flowViewport.offsetX = this.flowPanState.offsetX + point.x - this.flowPanState.point.x
        this.flowViewport.offsetY = this.flowPanState.offsetY + point.y - this.flowPanState.point.y
        this.drawFlowCanvas()
      }
    },
    onFlowPointerEnd(event) {
      if (!this.flowPointers[event.pointerId]) return
      this.$delete(this.flowPointers, event.pointerId)
      const pointers = this.getFlowPointers()
      if (pointers.length === 1) {
        this.flowPanState = { point: pointers[0], offsetX: this.flowViewport.offsetX, offsetY: this.flowViewport.offsetY }
        this.flowPinchState = null
      } else {
        this.flowPanState = null
        this.flowPinchState = null
        this.flowDragging = false
      }
    },
    createPinchState(pointers) {
      const center = this.flowPointerCenter(pointers)
      return {
        distance: this.flowPointerDistance(pointers),
        scale: this.flowViewport.scale,
        worldX: (center.x - this.flowViewport.offsetX) / this.flowViewport.scale,
        worldY: (center.y - this.flowViewport.offsetY) / this.flowViewport.scale
      }
    },
    flowPointerCenter(pointers) {
      return { x: (pointers[0].x + pointers[1].x) / 2, y: (pointers[0].y + pointers[1].y) / 2 }
    },
    flowPointerDistance(pointers) {
      return Math.hypot(pointers[0].x - pointers[1].x, pointers[0].y - pointers[1].y) || 1
    },
    onFlowWheel(event) {
      this.zoomFlowAt(this.getFlowCanvasPoint(event), Math.exp(-event.deltaY * 0.0012))
    },
    onFlowKeydown(event) {
      if (event.key === "+" || event.key === "=") this.zoomFlow(1.2)
      else if (event.key === "-") this.zoomFlow(1 / 1.2)
      else if (event.key === "0") this.resetFlowCanvas()
      else if (event.key === "ArrowUp") { this.flowViewport.offsetY += 28; this.drawFlowCanvas() }
      else if (event.key === "ArrowDown") { this.flowViewport.offsetY -= 28; this.drawFlowCanvas() }
      else if (event.key === "ArrowLeft") { this.flowViewport.offsetX += 28; this.drawFlowCanvas() }
      else if (event.key === "ArrowRight") { this.flowViewport.offsetX -= 28; this.drawFlowCanvas() }
    },
    drawFlowCanvas() {
      const canvas = this.$refs.flowCanvas
      if (!canvas || !this.flowCanvasSize.width) return
      const context = canvas.getContext("2d")
      const { width, height, dpr } = this.flowCanvasSize
      context.setTransform(dpr, 0, 0, dpr, 0, 0)
      context.clearRect(0, 0, width, height)
      context.fillStyle = "#fbfcfd"
      context.fillRect(0, 0, width, height)
      this.drawFlowGrid(context, width, height)
      context.save()
      context.translate(this.flowViewport.offsetX, this.flowViewport.offsetY)
      context.scale(this.flowViewport.scale, this.flowViewport.scale)
      this.flowDiagramEdges.forEach(edge => this.drawFlowEdge(context, edge))
      this.flowDiagramNodes.forEach(node => this.drawFlowNode(context, node))
      context.restore()
    },
    drawFlowGrid(context, width, height) {
      context.fillStyle = "rgba(117, 138, 153, 0.12)"
      for (let x = 18; x < width; x += 24) {
        for (let y = 18; y < height; y += 24) {
          context.fillRect(x, y, 1, 1)
        }
      }
    },
    drawFlowEdge(context, edge) {
      context.save()
      context.strokeStyle = edge.feedback ? "#88a7ba" : "#778e9b"
      context.lineWidth = 2
      if (edge.feedback) context.setLineDash([7, 6])
      context.beginPath()
      context.moveTo(edge.from.x, edge.from.y)
      if (edge.control1 && edge.control2) context.bezierCurveTo(edge.control1.x, edge.control1.y, edge.control2.x, edge.control2.y, edge.to.x, edge.to.y)
      else context.lineTo(edge.to.x, edge.to.y)
      context.stroke()
      context.setLineDash([])
      const directionPoint = edge.control2 || edge.from
      this.drawFlowArrow(context, directionPoint, edge.to, edge.feedback ? "#88a7ba" : "#778e9b")
      if (edge.label) {
        context.fillStyle = "#526d7c"
        context.font = "700 15px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        context.textAlign = "center"
        context.textBaseline = "middle"
        context.fillText(edge.label, edge.labelX, edge.labelY)
      }
      context.restore()
    },
    drawFlowArrow(context, from, to, color) {
      const angle = Math.atan2(to.y - from.y, to.x - from.x)
      const size = 10
      context.fillStyle = color
      context.beginPath()
      context.moveTo(to.x, to.y)
      context.lineTo(to.x - size * Math.cos(angle - Math.PI / 6), to.y - size * Math.sin(angle - Math.PI / 6))
      context.lineTo(to.x - size * Math.cos(angle + Math.PI / 6), to.y - size * Math.sin(angle + Math.PI / 6))
      context.closePath()
      context.fill()
    },
    drawFlowNode(context, node) {
      const colors = {
        source: ["#eaf5fb", "#629bc2"], process: ["#f8fbfd", "#8aa8bb"], data: ["#eff6ff", "#749dcc"],
        feature: ["#f1f7ef", "#80a776"], model: ["#edf8f4", "#5d9e83"], evaluate: ["#f7f2ff", "#9879bf"],
        output: ["#eaf5fb", "#5e95ba"], exception: ["#fff3ee", "#c77e69"], decision: ["#fff7df", "#c79e48"]
      }
      const [fill, stroke] = colors[node.type] || colors.process
      context.save()
      context.fillStyle = fill
      context.strokeStyle = stroke
      context.lineWidth = 2
      context.shadowColor = "rgba(42, 65, 80, 0.08)"
      context.shadowBlur = 8
      context.shadowOffsetY = 2
      if (node.type === "decision") {
        const centerX = node.x + node.width / 2
        const centerY = node.y + node.height / 2
        context.beginPath()
        context.moveTo(centerX, node.y)
        context.lineTo(node.x + node.width, centerY)
        context.lineTo(centerX, node.y + node.height)
        context.lineTo(node.x, centerY)
        context.closePath()
        context.fill()
        context.stroke()
      } else {
        this.drawRoundedRect(context, node.x, node.y, node.width, node.height, 7)
        context.fill()
        context.stroke()
      }
      context.shadowColor = "transparent"
      context.fillStyle = "#243444"
      context.textAlign = "center"
      context.textBaseline = "middle"
      context.font = "700 17px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      const textLayout = this.getFlowNodeTextLayout(node)
      context.fillText(node.title, node.x + node.width / 2, textLayout.titleY)
      context.fillStyle = "#5e7180"
      context.font = "14px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      node.lines.forEach((line, index) => context.fillText(line, node.x + node.width / 2, textLayout.descriptionY + index * 22))
      context.restore()
    },
    getFlowNodeTextLayout(node) {
      const titleHeight = 20
      const titleToDescriptionGap = 10
      const descriptionHeight = 16
      const descriptionLineGap = 22
      const lineCount = node.lines.length
      const totalHeight = titleHeight + titleToDescriptionGap + descriptionHeight + (lineCount - 1) * descriptionLineGap
      const top = node.y + (node.height - totalHeight) / 2
      return {
        titleY: top + titleHeight / 2,
        descriptionY: top + titleHeight + titleToDescriptionGap + descriptionHeight / 2
      }
    },
    drawRoundedRect(context, x, y, width, height, radius) {
      context.beginPath()
      context.moveTo(x + radius, y)
      context.lineTo(x + width - radius, y)
      context.quadraticCurveTo(x + width, y, x + width, y + radius)
      context.lineTo(x + width, y + height - radius)
      context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
      context.lineTo(x + radius, y + height)
      context.quadraticCurveTo(x, y + height, x, y + height - radius)
      context.lineTo(x, y + radius)
      context.quadraticCurveTo(x, y, x + radius, y)
      context.closePath()
    },
    async runPrediction() {
      const horizon = Number(this.predictionHorizonDays)
      if (!Number.isInteger(horizon) || horizon < 1 || horizon > 30) {
        this.predictionError = "预测周期需填写 1 到 30 天的整数"
        return
      }
      this.predicting = true
      this.prediction = null
      this.predictionError = ""
      try {
        const result = await predictSnakeWeightTrend(Number(this.selectedPetId), horizon)
        this.prediction = result.data || null
      } catch (error) {
        this.predictionError = error.message || "趋势预测失败"
      } finally {
        this.predicting = false
      }
    },
    isSnakePet(pet) {
      const group = String(pet.speciesGroupCode || "").trim().toLowerCase()
      const code = String(pet.speciesCode || "").trim().toLowerCase()
      const species = String(pet.species || "")
      return group === "snake" || group.includes("snake") || code.includes("snake") || species.includes("蛇")
    },
    statusText(status) { return status === "ACTIVE" ? "运行中" : "未启用" },
    runStatusText(status) { return { RUNNING: "运行中", SUCCEEDED: "已完成", FAILED: "失败" }[status] || "未知" },
    runStatusClass(status) { return `run-status--${String(status || "").toLowerCase()}` },
    modelName(value) { return { ridge_regression: "岭回归", mean_baseline: "均值基线" }[value] || value || "-" },
    candidateKey(candidate) {
      return `${candidate.modelType || "candidate"}-${JSON.stringify(candidate.hyperparameters || {})}`
    },
    isSelectedCandidate(candidate, run) {
      return candidate.modelType === run.selectedModel
        && JSON.stringify(candidate.hyperparameters || {}) === JSON.stringify(run.selectedHyperparameters || {})
    },
    formatHyperparameters(value) {
      const entries = value && typeof value === "object" ? Object.entries(value) : []
      return entries.length ? entries.map(([key, item]) => `${key}=${item}`).join(" · ") : "无额外参数"
    },
    predictionSourceText(source) {
      return source === "PUBLISHED_MODEL" ? "已发布模型预测" : "本地线性趋势"
    },
    formatMetric(value) { return value == null ? "-" : `${Number(value).toFixed(4)} g/天` },
    formatFileSize(value) {
      const bytes = Number(value)
      if (!Number.isFinite(bytes) || bytes < 1024) return `${Number.isFinite(bytes) ? bytes : 0} B`
      return `${(bytes / 1024).toFixed(1)} KB`
    },
    confidenceText(value) { return { HIGH: "高", MEDIUM: "中", LOW: "低" }[value] || "未知" },
    confidenceClass(value) { return value ? `confidence-badge--${value.toLowerCase()}` : "" },
    trendClass(value) { return value === "DOWN" ? "metric--down" : value === "UP" ? "metric--up" : "" },
    formatWeight(value) { return value == null ? "-" : `${Number(value).toFixed(2)} g` },
    signedWeight(value) {
      if (value == null) return "-"
      const number = Number(value)
      return `${number > 0 ? "+" : ""}${number.toFixed(2)} g`
    },
    signedDailyWeight(value) {
      if (value == null) return "-"
      const number = Number(value)
      return `${number > 0 ? "+" : ""}${number.toFixed(4)} g/天`
    },
    formatRatio(value) { return value == null ? "-" : Number(value).toFixed(3) },
    formatDate(value) { return value ? String(value).slice(0, 10) : "-" },
    formatDateTime(value) { return value ? String(value).replace("T", " ").slice(0, 16) : "-" }
  }
}
</script>

<style scoped>
.machine-learning-page { max-width: 1240px; margin: 0 auto; padding-bottom: 28px; }
.page-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; margin: 10px 0 20px; }
.page-head h2 { margin: 0; color: #172033; font-size: 24px; letter-spacing: 0; }
.page-head p { margin: 7px 0 0; color: #738095; font-size: 13px; }
.algorithm-count { display: flex; align-items: baseline; gap: 7px; color: #7b8798; font-size: 12px; }
.algorithm-count strong { color: #20654c; font-size: 26px; line-height: 1; }
.machine-learning-workspace { display: grid; grid-template-columns: 270px minmax(0, 1fr); min-height: 660px; background: #fff; border: 1px solid #e2e8ee; border-radius: 8px; overflow: hidden; }
.algorithm-list { padding: 18px 12px; background: #f8fafb; border-right: 1px solid #e5eaef; }
.algorithm-list__head { display: flex; align-items: center; justify-content: space-between; padding: 0 8px 13px; color: #233042; }
.algorithm-list__head strong { font-size: 13px; }
.algorithm-list__head span { color: #86919f; font-size: 11px; }
.algorithm-item { width: 100%; display: flex; flex-direction: column; gap: 8px; padding: 13px; border: 1px solid transparent; border-radius: 6px; background: transparent; color: #283649; text-align: left; cursor: pointer; }
.algorithm-item:hover { background: #fff; border-color: #e0e7e4; }
.algorithm-item--active { background: #fff; border-color: #a8cbbb; box-shadow: 0 4px 12px rgba(27, 70, 54, .06); }
.algorithm-item__topline { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.algorithm-type { color: #6f7c8d; font-size: 10px; }
.status-badge, .version-badge, .confidence-badge { display: inline-flex; align-items: center; width: fit-content; border-radius: 999px; font-size: 10px; font-weight: 700; line-height: 1; white-space: nowrap; }
.status-badge { padding: 5px 7px; background: #e8f5ed; color: #267050; }
.algorithm-item strong { color: #1d2a3a; font-size: 14px; line-height: 1.35; }
.algorithm-item small { color: #718096; font-size: 11px; line-height: 1.55; }
.algorithm-list__empty { padding: 24px 12px; color: #83909d; font-size: 12px; text-align: center; }
.algorithm-detail { min-width: 0; padding: 26px 30px 34px; }
.algorithm-overview { padding-bottom: 24px; border-bottom: 1px solid #e7ecf0; }
.algorithm-overview__title { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.eyebrow, .section-index { color: #267154; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.algorithm-overview h3 { margin: 7px 0 0; color: #1c2938; font-size: 23px; letter-spacing: 0; }
.version-badge { padding: 7px 9px; background: #eef3f1; color: #477061; }
.algorithm-overview > p { max-width: 780px; margin: 14px 0 0; color: #536173; font-size: 14px; line-height: 1.75; }
.overview-meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin: 20px 0 0; }
.overview-meta div { min-width: 0; padding-left: 12px; border-left: 2px solid #9fc7b4; }
.overview-meta dt { color: #7a8795; font-size: 11px; }
.overview-meta dd { margin: 6px 0 0; color: #354458; font-size: 12px; line-height: 1.55; }
.detail-section { padding: 25px 0; border-bottom: 1px solid #e7ecf0; }
.detail-section__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; color: #82909e; font-size: 11px; }
.detail-section__head > div { display: flex; align-items: baseline; gap: 10px; }
.detail-section h4 { margin: 0; color: #263345; font-size: 16px; letter-spacing: 0; }
.flow-canvas-panel { overflow: hidden; border: 1px solid #dce6ed; border-radius: 6px; background: #f7fafc; }
.flow-canvas-toolbar { min-height: 42px; display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding: 0 10px; border-bottom: 1px solid #dce6ed; background: #fff; }
.flow-canvas-zoom { min-width: 42px; color: #657486; font-size: 11px; font-variant-numeric: tabular-nums; text-align: center; }
.flow-canvas-controls { display: flex; align-items: center; gap: 5px; }
.flow-canvas-button { width: 28px; height: 28px; display: grid; place-items: center; padding: 0; border: 1px solid #d6e0e6; border-radius: 4px; background: #fff; color: #3e596b; font-size: 17px; line-height: 1; cursor: pointer; }
.flow-canvas-button:hover { border-color: #75a0b8; background: #eef6fa; color: #1f607f; }
.flow-canvas-button:focus-visible { outline: 2px solid #75a0b8; outline-offset: 1px; }
.flow-canvas-legend { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 15px; min-height: 34px; padding: 6px 12px; border-bottom: 1px solid #e4ebef; background: #fbfcfd; color: #6b7a89; font-size: 10px; }
.flow-canvas-legend span { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.flow-legend-swatch { width: 8px; height: 8px; display: inline-block; border: 1px solid; border-radius: 2px; }
.flow-legend-swatch--data { border-color: #749dcc; background: #eff6ff; }
.flow-legend-swatch--model { border-color: #5d9e83; background: #edf8f4; }
.flow-legend-swatch--evaluate { border-color: #9879bf; background: #f7f2ff; }
.flow-legend-swatch--feedback { border-color: #88a7ba; border-style: dashed; background: #f7fafc; }
.flow-canvas { width: 100%; height: 640px; display: block; cursor: grab; outline: none; touch-action: none; }
.flow-canvas:focus-visible { box-shadow: inset 0 0 0 2px #75a0b8; }
.flow-canvas--dragging { cursor: grabbing; }
.flow-diagram-note { margin: 12px 0 0; color: #718095; font-size: 11px; line-height: 1.6; }
.training-steps { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 16px; border: 1px solid #dfe8e5; border-radius: 6px; overflow: hidden; }
.training-steps div { display: flex; align-items: center; gap: 8px; min-height: 48px; padding: 0 12px; border-right: 1px solid #dfe8e5; background: #fbfdfc; color: #5e6e7d; font-size: 11px; }
.training-steps div:last-child { border-right: 0; }
.training-steps strong { color: #267154; font-size: 12px; font-variant-numeric: tabular-nums; }
.dataset-form { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto; align-items: stretch; gap: 10px; }
.dataset-picker { position: relative; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 2px 9px; min-height: 64px; padding: 10px 12px; border: 1px dashed #aabbb5; border-radius: 6px; background: #fcfdfd; color: #5f7180; cursor: pointer; }
.dataset-picker:hover, .dataset-picker--ready { border-color: #579477; background: #f7fbf8; }
.dataset-picker input { position: absolute; z-index: 1; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.dataset-picker > span { grid-row: 1 / span 2; align-self: center; color: #2d6f52; font-size: 11px; font-weight: 800; }
.dataset-picker > strong { overflow: hidden; color: #354659; font-size: 12px; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
.dataset-picker > small { color: #80909d; font-size: 10px; }
.dataset-form__submit { align-self: center; min-width: 132px; }
.training-readonly, .dataset-contract, .training-notice { margin: 12px 0 0; font-size: 11px; line-height: 1.6; }
.training-readonly, .dataset-contract { color: #728091; }
.dataset-contract code { padding: 1px 4px; border-radius: 3px; background: #eef3f4; color: #456170; }
.training-notice { color: #287151; }
.training-result { margin-top: 18px; padding: 15px; border: 1px solid #cce2d5; border-radius: 6px; background: #f8fcf9; }
.training-result__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.training-result__head div { display: grid; gap: 4px; }
.training-result__head span { color: #5f7f70; font-size: 11px; font-weight: 700; }
.training-result__head strong { color: #214634; font-size: 14px; }
.training-metrics { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; margin-top: 14px; }
.training-metrics div { min-width: 0; }
.training-metrics span { display: block; color: #708091; font-size: 10px; }
.training-metrics strong { display: block; margin-top: 5px; color: #263b2f; font-size: 14px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.training-result > p { margin: 14px 0 0; padding-top: 11px; border-top: 1px solid #dfece4; color: #6d7c8d; font-size: 11px; }
.model-release { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 14px; padding-top: 11px; border-top: 1px solid #dfece4; color: #6d7c8d; font-size: 11px; }
.model-release__published { color: #287151; font-weight: 700; }
.model-release__button, .history-publish-button { border: 1px solid #579477; border-radius: 4px; background: #fff; color: #277150; font-size: 11px; font-weight: 700; cursor: pointer; }
.model-release__button { min-height: 28px; padding: 0 9px; }
.history-publish-button { min-height: 24px; padding: 0 7px; }
.model-release__button:hover, .history-publish-button:hover { background: #edf8f1; }
.model-release__button:disabled, .history-publish-button:disabled { opacity: .55; cursor: not-allowed; }
.candidate-results { margin-top: 14px; padding-top: 11px; border-top: 1px solid #dfece4; }
.candidate-results > span { display: block; color: #5c7d6d; font-size: 10px; font-weight: 700; }
.candidate-results__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(154px, 1fr)); gap: 7px; margin-top: 8px; }
.candidate-results__grid > div { min-width: 0; padding: 8px 9px; border: 1px solid #dce7e1; border-radius: 5px; background: #fff; }
.candidate-results__grid > .candidate-result--selected { border-color: #6fa788; background: #f1f8f3; }
.candidate-results__grid strong, .candidate-results__grid small, .candidate-results__grid em { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.candidate-results__grid strong { color: #34475a; font-size: 11px; }
.candidate-results__grid small { margin-top: 3px; color: #82909d; font-size: 10px; }
.candidate-results__grid em { margin-top: 6px; color: #3f765b; font-size: 10px; font-style: normal; }
.training-charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px; margin-top: 16px; padding-top: 14px; border-top: 1px solid #dfece4; }
.training-chart { min-width: 0; }
.training-chart--wide { grid-column: 1 / -1; }
.training-chart__head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; color: #34475a; font-size: 11px; }
.training-chart__head strong { font-size: 11px; }
.training-chart__head span { color: #7d8c99; font-size: 10px; }
.training-chart__canvas { width: 100%; height: 218px; margin-top: 8px; }
.training-chart__canvas--profile { height: 276px; }
.training-chart__note { margin: 6px 0 0; color: #718095; font-size: 10px; line-height: 1.55; }
.run-status { display: inline-flex; align-items: center; padding: 4px 6px; border-radius: 999px; font-size: 10px; font-weight: 700; line-height: 1; white-space: nowrap; }
.run-status--succeeded { background: #ddefe4; color: #227050; }
.run-status--running { background: #e5f0f7; color: #347093; }
.run-status--failed { background: #fbe3df; color: #a14d44; }
.run-status--published { background: #ddefe4; color: #227050; }
.training-history { margin-top: 20px; border-top: 1px solid #e7ecf0; }
.training-history__head { display: flex; align-items: center; justify-content: space-between; min-height: 42px; color: #4d5d6e; font-size: 12px; }
.training-history__head button { width: 28px; height: 28px; padding: 0; border: 1px solid #d8e1e5; border-radius: 4px; background: #fff; color: #537083; font-size: 15px; cursor: pointer; }
.training-history__head button:disabled { opacity: .5; cursor: not-allowed; }
.training-history__empty { padding: 18px 10px; border-top: 1px solid #edf0f2; color: #84909c; font-size: 11px; text-align: center; }
.training-history__table-wrap { overflow-x: auto; border-top: 1px solid #edf0f2; }
.training-history table { width: 100%; min-width: 820px; border-collapse: collapse; }
.training-history th, .training-history td { padding: 10px 8px; border-bottom: 1px solid #edf0f2; color: #58697a; font-size: 11px; line-height: 1.45; text-align: left; vertical-align: top; }
.training-history th { color: #81909d; font-size: 10px; font-weight: 700; }
.training-history td strong, .training-history td small { display: block; max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.training-history td strong { color: #34475a; font-size: 11px; }
.training-history td small { margin-top: 3px; color: #86939f; font-size: 10px; }
.prediction-form { display: grid; grid-template-columns: minmax(200px, 1fr) 156px auto; align-items: end; gap: 12px; }
.prediction-form label { display: block; min-width: 0; }
.prediction-form label > span { display: block; margin-bottom: 7px; color: #708091; font-size: 11px; font-weight: 700; }
.prediction-form select, .prediction-form input { width: 100%; height: 38px; padding: 0 10px; border: 1px solid #d6dfe0; border-radius: 5px; background: #fff; color: #2f4050; font-size: 12px; outline: none; }
.prediction-form select:focus, .prediction-form input:focus { border-color: #579477; box-shadow: 0 0 0 3px rgba(87, 148, 119, .12); }
.run-button { height: 38px; padding: 0 16px; border: 0; border-radius: 5px; background: #277150; color: #fff; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.run-button:disabled { opacity: .55; cursor: not-allowed; }
.prediction-error { margin: 12px 0 0; color: #a8463a; font-size: 12px; }
.prediction-result { margin-top: 18px; padding: 16px; border: 1px solid #cce2d5; border-radius: 6px; background: #f7fbf8; }
.prediction-result__headline { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.prediction-result__headline span { color: #5c7d6d; font-size: 11px; font-weight: 700; }
.prediction-result__headline strong { color: #244635; font-size: 13px; }
.prediction-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 15px; }
.prediction-metrics div { min-width: 0; }
.prediction-metrics span { display: block; color: #708091; font-size: 11px; }
.prediction-metrics strong { display: block; margin-top: 6px; color: #263b2f; font-size: 16px; line-height: 1.2; white-space: nowrap; }
.prediction-metrics .metric--up { color: #228158; }
.prediction-metrics .metric--down { color: #b5574e; }
.prediction-result__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 15px; padding-top: 12px; border-top: 1px solid #dfece4; color: #718095; font-size: 11px; }
.prediction-chart { margin-top: 15px; padding-top: 12px; border-top: 1px solid #dfece4; }
.prediction-chart__canvas { width: 100%; height: 240px; margin-top: 8px; }
.confidence-badge { padding: 6px 8px; }
.confidence-badge--high { background: #ddefe4; color: #227050; }
.confidence-badge--medium { background: #fff2cf; color: #946d17; }
.confidence-badge--low { background: #fbe3df; color: #a14d44; }
.prediction-note { margin: 12px 0 0; color: #7b8795; font-size: 11px; line-height: 1.55; }
.limitations-section { border-bottom: 0; }
.limitations-section ul { display: grid; gap: 10px; margin: 0; padding: 0; list-style: none; }
.limitations-section li { position: relative; padding-left: 15px; color: #627184; font-size: 12px; line-height: 1.6; }
.limitations-section li::before { position: absolute; top: 8px; left: 1px; width: 5px; height: 5px; border-radius: 50%; background: #79a18e; content: ""; }
.page-state { padding: 50px 18px; color: #718094; text-align: center; font-size: 13px; }
.page-state--error { color: #a8463a; }
@media (max-width: 850px) { .machine-learning-workspace { grid-template-columns: 1fr; } .algorithm-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 8px; border-right: 0; border-bottom: 1px solid #e5eaef; } .algorithm-list__head { grid-column: 1 / -1; padding-bottom: 3px; } .algorithm-detail { padding: 22px 18px 28px; } }
@media (max-width: 760px) { .training-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); } .training-steps div:nth-child(2) { border-right: 0; } .training-steps div:nth-child(-n+2) { border-bottom: 1px solid #dfe8e5; } .dataset-form { grid-template-columns: 1fr; } .dataset-form__submit { width: 100%; } .training-metrics, .training-charts { grid-template-columns: 1fr; } }
@media (max-width: 620px) { .page-head { align-items: flex-start; flex-direction: column; } .overview-meta, .prediction-metrics { grid-template-columns: 1fr 1fr; } .flow-canvas { height: 500px; } .prediction-form { grid-template-columns: 1fr; } .run-button { width: 100%; } .prediction-result__headline, .prediction-result__foot { align-items: flex-start; flex-direction: column; } }
</style>
